import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import { createFamily, deleteFamily, getEmployeeFamily, loadEmployeeUser, masters, uploadToModule } from "./api";
import {
  DEPENDENT_DOCUMENTS_NOTE,
  FALLBACK_DEPENDENCY,
  FALLBACK_EMPLOYMENT,
  FALLBACK_GENDER,
  FALLBACK_MARITAL,
  FALLBACK_NATIONALITY,
  FALLBACK_REASON_ROOF,
  FALLBACK_RELATIONSHIP,
  FALLBACK_ROOF,
} from "./constants";
import {
  dmyToYmd,
  employeeProfile,
  emptySlot,
  formatDob,
  getEmploymentCategory,
  getSessionUser,
  isFamilyPhoto,
  phoneOk,
  toISODate,
  type Option,
  type UploadSlot,
  type UploadedDoc,
} from "./helpers";
import {
  ActionBar,
  DateField,
  DocUploader,
  Field,
  PageShell,
  Section,
  SelectInput,
  SubtitleBanner,
  TextArea,
  TextInput,
  UploadSlotButton,
} from "./ui";
import { BRAND } from "./constants";

type Dep = { id: string; firstName: string; lastName: string; relation: string; dob: string; startDate: string };

const MODULE = "sap_inclusion_of_dependents_family";

const FamilyMembersForm = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(getSessionUser());
  const profile = employeeProfile(user);
  const [contactNo, setContactNo] = useState(profile.contactNo);
  const [addFamilyMember, setAddFamilyMember] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [nationality, setNationality] = useState("");
  const [dependencyType, setDependencyType] = useState<string | null>(null);
  const [employmentStatus, setEmploymentStatus] = useState("");
  const [marriageDate, setMarriageDate] = useState("");
  const [maritalStatusOfChild, setMaritalStatusOfChild] = useState("");
  const [personnelNumber, setPersonnelNumber] = useState("");
  const [nameOfState, setNameOfState] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [monthlyIncome, setMonthlyIncome] = useState("");
  const [stayingRoof, setStayingRoof] = useState("");
  const [reasonNotStaying, setReasonNotStaying] = useState("");
  const [remarks, setRemarks] = useState("");
  const [photoSlot, setPhotoSlot] = useState<UploadSlot>(emptySlot());
  const [documents, setDocuments] = useState<UploadedDoc[]>([]);
  const [deletionDocuments, setDeletionDocuments] = useState<UploadedDoc[]>([]);
  const [deletionReason, setDeletionReason] = useState("");
  const [selectedDependentId, setSelectedDependentId] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<any[]>([]);

  const [relationshipOptions, setRelationshipOptions] = useState<Option[]>(FALLBACK_RELATIONSHIP);
  const [genderOptions, setGenderOptions] = useState<Option[]>(FALLBACK_GENDER);
  const [nationalityOptions, setNationalityOptions] = useState<Option[]>(FALLBACK_NATIONALITY);
  const [dependencyTypeOptions, setDependencyTypeOptions] = useState<Option[]>(FALLBACK_DEPENDENCY);
  const [employmentStatusOptions, setEmploymentStatusOptions] = useState<Option[]>(FALLBACK_EMPLOYMENT);
  const [stayingRoofOptions, setStayingRoofOptions] = useState<Option[]>(FALLBACK_ROOF);
  const [reasonNotStayingOptions, setReasonNotStayingOptions] = useState<Option[]>(FALLBACK_REASON_ROOF);
  const [maritalStatusOptions, setMaritalStatusOptions] = useState<Option[]>(FALLBACK_MARITAL);

  useEffect(() => {
    loadEmployeeUser().then((u) => {
      setUser(u);
      const p = employeeProfile(u);
      setContactNo((prev) => prev || p.contactNo);
    });
  }, []);

  useEffect(() => {
    (async () => {
      const [rel, gen, nat, dep, emp, roof, reason, marital] = await Promise.all([
        masters.relationship(),
        masters.gender(),
        masters.nationality(),
        masters.dependencyType(),
        masters.employment(),
        masters.roof(),
        masters.reasonRoof(),
        masters.marital(),
      ]);
      if (rel.length) setRelationshipOptions(rel);
      if (gen.length) setGenderOptions(gen);
      if (nat.length) setNationalityOptions(nat);
      if (dep.length) setDependencyTypeOptions(dep);
      if (emp.length) setEmploymentStatusOptions(emp);
      if (roof.length) setStayingRoofOptions(roof);
      if (reason.length) setReasonNotStayingOptions(reason);
      if (marital.length) setMaritalStatusOptions(marital);
    })().catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!profile.employeeNo) return;
    getEmployeeFamily(profile.employeeNo)
      .then((res) => {
        if (res.ok) setFamilyMembers(res.list);
        else {
          setFamilyMembers([]);
          toast.error(res.message || "Something went wrong");
        }
      })
      .catch(() => {
        setFamilyMembers([]);
        toast.error("Something went wrong");
      });
  }, [profile.employeeNo]);

  const relationshipLabel = (relationshipOptions.find((o) => o.id === relationship)?.label || "").toLowerCase();
  const isSpouse = relationshipLabel.includes("spouse");
  const isChild = relationshipLabel.includes("child");
  const spouse = profile.spouseMember;
  const spouseName = profile.spouseName;
  const spouseDependencyStatus = spouse
    ? dependencyTypeOptions.find((o) => o.id === String(spouse.dependencyType ?? ""))?.label ?? ""
    : "";
  const stayingRoofLabel = (stayingRoofOptions.find((o) => o.id === stayingRoof)?.label || "").toLowerCase();
  const isNotStaying = stayingRoofLabel === "no";
  const employmentLabel = employmentStatusOptions.find((o) => o.id === employmentStatus)?.label || "";
  const employmentCategory = getEmploymentCategory(employmentLabel);

  const dependentOptions: Dep[] = useMemo(() => {
    const first = (m: any, keys: string[]) => {
      for (const k of keys) if (m?.[k] !== undefined && m?.[k] !== null && m?.[k] !== "") return m[k];
      return undefined;
    };
    const relationName = (m: any) => {
      const named = first(m, ["relation", "Relation", "relationship", "Relationship", "RelationshipText"]);
      if (named) return String(named);
      const code = String(first(m, ["Subtype", "subtype"]) ?? "");
      return ({ "1": "Spouse", "2": "Child" } as Record<string, string>)[code] ?? relationshipOptions.find((o) => o.id === code)?.label ?? "";
    };
    const normalizeDob = (raw: any) => {
      const s = String(raw ?? "").trim();
      const m2 = s.match(/^(\d{2})[.\-/](\d{2})[.\-/](\d{2})$/);
      if (m2) {
        const yy = Number(m2[3]);
        const cur = new Date().getFullYear() % 100;
        return `${m2[1]}.${m2[2]}.${yy > cur ? 1900 + yy : 2000 + yy}`;
      }
      return s;
    };
    return familyMembers
      .map((m: any, idx: number) => ({ m, idx, relation: relationName(m) }))
      .filter(({ relation }) => ["spouse", "child"].includes(relation.toLowerCase()))
      .map(({ m, idx, relation }) => ({
        id: String(first(m, ["id", "Id", "ObjectID"]) ?? `${first(m, ["Subtype", "subtype"]) ?? ""}-${m?.Seqnr ?? ""}-${idx}`),
        firstName: String(first(m, ["firstname", "firstName", "FirstName", "Firstname"]) ?? ""),
        lastName: String(first(m, ["lastname", "lastName", "LastName", "Lastname"]) ?? ""),
        relation,
        dob: formatDob(normalizeDob(first(m, ["birthDate", "BirthDate", "birthdate", "DateOfBirth", "dob"]))),
        startDate: formatDob(first(m, ["startDate", "StartDate", "startdate", "start_date", "dependencyStartDate"])),
      }));
  }, [familyMembers, relationshipOptions]);

  const selectedDependent = dependentOptions.find((d) => d.id === selectedDependentId) ?? null;

  const uploadPhoto = async (file: File) => {
    if (!isFamilyPhoto(file)) {
      toast.error("Only PNG, JPG or JPEG files are allowed");
      return;
    }
    setPhotoSlot((p) => ({ ...p, isUploading: true }));
    try {
      const res = await uploadToModule(file, MODULE, file.type || "application/octet-stream");
      if (res.ok) {
        setPhotoSlot({ fileName: file.name, fileExtension: file.type || "image/jpeg", key: res.key, isUploading: false });
        toast.success("File uploaded successfully");
      } else {
        setPhotoSlot((p) => ({ ...p, isUploading: false }));
        toast.error(res.message || "Something went wrong");
      }
    } catch {
      setPhotoSlot((p) => ({ ...p, isUploading: false }));
      toast.error("Something went wrong");
    }
  };

  const addDoc = async (file: File, forDeletion: boolean) => {
    const res = await uploadToModule(file, MODULE, file.type || "application/octet-stream");
    if (!res.ok) {
      toast.error(res.message || "Something went wrong");
      return;
    }
    const doc = { fileName: file.name, fileExtension: file.type || "application/octet-stream", key: res.key };
    if (forDeletion) setDeletionDocuments((p) => [...p, doc]);
    else setDocuments((p) => [...p, doc]);
  };

  const handleSave = async () => {
    if (addFamilyMember === "Deletion") {
      const missing: string[] = [];
      if (!selectedDependentId) missing.push("Please select a dependent to delete");
      if (deletionDocuments.length === 0) missing.push("Please upload required documents");
      if (missing.length) {
        toast.error(missing.join("\n"));
        return;
      }
      setSaving(true);
      try {
        const relCode =
          relationshipOptions.find((o) => o.label.toLowerCase() === (selectedDependent?.relation ?? "").toLowerCase())?.id ??
          selectedDependent?.relation ??
          "";
        const payload = {
          Action: "C",
          ReferenceNumber: "",
          EmpID: profile.employeeNo,
          PersonnelNumber: profile.employeeNo,
          EmployeeName: profile.employeeName,
          Designation: profile.designation,
          PayScale: profile.payScale,
          FamilyMembersAdditionDeletion: "1",
          RelativeFirstName: selectedDependent?.firstName ?? "",
          RelativeLastName: selectedDependent?.lastName ?? "",
          DateOfBirth: dmyToYmd(selectedDependent?.dob),
          Relationship: relCode,
          ContactNumber: contactNo,
          Remarks: deletionReason,
          ToDocuments: {
            results: deletionDocuments.map((d) => ({
              FileName: d.fileName,
              FileExtension: d.fileExtension,
              Content: d.key,
              TypeOfDocument: "D",
            })),
          },
        };
        const res = await deleteFamily(payload);
        if (res?.success) {
          toast.success(res?.message || "Request Submitted Successfully.");
          navigate(all_routes.idCard.familyMemberInclusion);
        } else toast.error(res?.message || "Something went wrong");
      } catch {
        toast.error("Something went wrong");
      } finally {
        setSaving(false);
      }
      return;
    }

    const e: Record<string, string> = {};
    const missing: string[] = [];
    if (!firstName.trim()) missing.push((e.firstName = "Please enter first name"));
    if (!relationship) missing.push("please_select_relationship");
    if (!gender) missing.push("Please select gender");
    if (!dob.trim()) missing.push("Please select date of birth");
    if (!nationality) missing.push("Please select nationality");
    if (dependencyType === null) missing.push("Please select dependency type");
    if (!employmentStatus) missing.push("Please select employment status of dependent");
    if (!contactNo.trim()) missing.push((e.contactNo = "Contact No is required"));
    else if (!phoneOk(contactNo)) missing.push((e.contactNo = "Invalid phone number (10-15 digits, optional +)"));
    if (!photoSlot.key) missing.push("Please upload photo");
    if (documents.length === 0) missing.push("Please upload required documents");
    setErrors(e);
    if (missing.length) {
      toast.error(missing.join("\n"));
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, any> = {
        Action: "C",
        ReferenceNumber: "",
        EmpID: profile.employeeNo,
        FamilyMembersAdditionDeletion: "0",
        EmployeeName: profile.employeeName,
        Designation: profile.designation,
        RelativeFirstName: firstName,
        RelativeLastName: lastName,
        Relationship: relationship,
        Gender: gender,
        DateOfBirth: toISODate(dob),
        DependencyType: dependencyType,
        Nationality: nationality,
        EmployeeStatus: employmentStatus,
        StayingRoof: stayingRoof,
        ReasonForNotStaying: isNotStaying ? reasonNotStaying : "",
        ContactNumber: contactNo,
        Remarks: remarks,
        ToDocuments: {
          results: documents.map((d) => ({
            FileName: d.fileName,
            FileExtension: d.fileExtension,
            key: d.key,
            TypeOfDocument: "D",
          })),
        },
        ToPictures: {
          results: photoSlot.key
            ? [{ FileName: photoSlot.fileName, FileExtension: photoSlot.fileExtension, key: photoSlot.key, TypeOfDocument: "P" }]
            : [],
        },
      };
      if (isSpouse) payload.MarriedDate = toISODate(marriageDate);
      if (isChild) payload.MaritalStatusOfChild = maritalStatusOfChild;
      if (employmentCategory === "dmrc") payload.PersonnelNumber = personnelNumber;
      if (employmentCategory === "govt") {
        payload.NameOfState = nameOfState;
        payload.OrganizationName = organizationName;
        payload.MonthlyIncome = monthlyIncome;
      }
      if (employmentCategory === "cpse_psu" || employmentCategory === "private") {
        payload.OrganizationName = organizationName;
        payload.MonthlyIncome = monthlyIncome;
      }
      const res = await createFamily(payload);
      if (res?.success) {
        toast.success(res?.message || "Request Submitted Successfully.");
        navigate(all_routes.idCard.familyMemberInclusion);
      } else toast.error(res?.message || "Something went wrong");
    } catch {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageShell title="Inclusion and Deletion">
      <SubtitleBanner text="List of Inclusion / Deletion of family members (Spouse and Children)" />
      <Section title="Employee Details">
        <Field label="Employee No.">
          <TextInput value={profile.employeeNo} disabled readOnly />
        </Field>
        <Field label="Employee Name">
          <TextInput value={profile.employeeName} disabled readOnly />
        </Field>
        <Field label="Designation">
          <TextInput value={profile.designation} disabled readOnly />
        </Field>
        <Field label="Pay Scale">
          <TextInput value={profile.payScale} disabled readOnly />
        </Field>
        <Field label="Contact No." required error={errors.contactNo}>
          <TextInput value={contactNo} placeholder="Enter Contact Number" onChange={(e) => setContactNo(e.target.value)} />
        </Field>
        {isChild && (
          <>
            <Field label="Spouse Name">
              <TextInput value={spouseName} disabled readOnly />
            </Field>
            <Field label="Spouse dependency status">
              <TextInput value={spouseDependencyStatus} disabled readOnly />
            </Field>
          </>
        )}
        <Field label="Add Family Member">
          <SelectInput
            value={addFamilyMember}
            placeholder="Select Option"
            options={[
              { id: "Addition", label: "Addition" },
              { id: "Deletion", label: "Deletion" },
            ]}
            onChange={setAddFamilyMember}
          />
        </Field>
      </Section>

      {addFamilyMember === "Addition" && (
        <Section title="Fill Details Of Dependents">
          <Field label="First Name" required error={errors.firstName}>
            <TextInput value={firstName} placeholder="Enter First Name" onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last Name">
            <TextInput value={lastName} placeholder="Enter Last Name" onChange={(e) => setLastName(e.target.value)} />
          </Field>
          <Field label="Relationship" required>
            <SelectInput value={relationship} placeholder="Select Relationship" options={relationshipOptions} onChange={setRelationship} />
          </Field>
          <Field label="Gender" required>
            <SelectInput value={gender} placeholder="Select an option" options={genderOptions} onChange={setGender} />
          </Field>
          <Field label="Date of Birth" required>
            <DateField value={dob} onChange={setDob} />
          </Field>
          <Field label="Nationality" required>
            <SelectInput value={nationality} placeholder="Select an option" options={nationalityOptions} onChange={setNationality} />
          </Field>
          <Field label="Dependency Type" required>
            <select
              className="form-select"
              style={{ background: "#F3F5FA", border: "1px solid #E2E8F0" }}
              value={dependencyType === null ? "__unset__" : dependencyType === "" ? "__blank__" : dependencyType}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "__unset__") setDependencyType(null);
                else if (v === "__blank__") setDependencyType("");
                else setDependencyType(v);
              }}
            >
              <option value="__unset__">Select an option</option>
              {dependencyTypeOptions.map((o) => (
                <option key={`${o.label}-${o.id}`} value={o.id === "" ? "__blank__" : o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Employment Status of Dependent" required>
            <SelectInput value={employmentStatus} placeholder="Select Employment Status" options={employmentStatusOptions} onChange={setEmploymentStatus} />
          </Field>
          {isSpouse && (
            <Field label="Marriage Date">
              <DateField value={marriageDate} onChange={setMarriageDate} />
            </Field>
          )}
          {isChild && (
            <Field label="Marital Status of dependent">
              <SelectInput value={maritalStatusOfChild} placeholder="Select an option" options={maritalStatusOptions} onChange={setMaritalStatusOfChild} />
            </Field>
          )}
          {employmentCategory === "dmrc" && (
            <Field label="If dependent is working with DMRC, his/her Emp.No.">
              <TextInput value={personnelNumber} placeholder="Enter Employee Id" onChange={(e) => setPersonnelNumber(e.target.value)} />
            </Field>
          )}
          {employmentCategory === "govt" && (
            <>
              <Field label="Name of the State">
                <TextInput value={nameOfState} placeholder="Enter name of the state" onChange={(e) => setNameOfState(e.target.value)} />
              </Field>
              <Field label="Organization name">
                <TextInput value={organizationName} placeholder="Enter organization name" onChange={(e) => setOrganizationName(e.target.value)} />
              </Field>
              <Field label="Monthly Income">
                <TextInput value={monthlyIncome} placeholder="Enter monthly income" onChange={(e) => setMonthlyIncome(e.target.value)} />
              </Field>
            </>
          )}
          {(employmentCategory === "cpse_psu" || employmentCategory === "private") && (
            <>
              <Field label="Organization name">
                <TextInput value={organizationName} placeholder="Enter organization name" onChange={(e) => setOrganizationName(e.target.value)} />
              </Field>
              <Field label="Monthly Income">
                <TextInput value={monthlyIncome} placeholder="Enter monthly income" onChange={(e) => setMonthlyIncome(e.target.value)} />
              </Field>
            </>
          )}
          <Field label="Staying under same roof">
            <SelectInput value={stayingRoof} placeholder="Select Option" options={stayingRoofOptions} onChange={setStayingRoof} />
          </Field>
          {isNotStaying && (
            <Field label="Reason for Not staying under the same roof">
              <SelectInput value={reasonNotStaying} placeholder="Select an option" options={reasonNotStayingOptions} onChange={setReasonNotStaying} />
            </Field>
          )}
          <Field label="Any Remark">
            <TextArea value={remarks} placeholder="Enter Remark" onChange={(e) => setRemarks(e.target.value)} />
          </Field>
          <UploadSlotButton
            required
            label="Upload Photo (.png, .jpg or .jpeg file type only)"
            slot={photoSlot}
            onPick={uploadPhoto}
            onClear={() => setPhotoSlot(emptySlot())}
          />
          <div className="fw-bold mb-1">Mandatory Attachments:</div>
          <div className="mb-3" style={{ whiteSpace: "pre-wrap", color: "#000", fontSize: 14 }}>
            {DEPENDENT_DOCUMENTS_NOTE}
          </div>
        </Section>
      )}

      {addFamilyMember === "Addition" && (
        <DocUploader
          required
          title="Upload Required Documents"
          docs={documents}
          onAdd={(f) => addDoc(f, false)}
          onRemove={(i) => setDocuments((p) => p.filter((_, idx) => idx !== i))}
        />
      )}

      {addFamilyMember === "Deletion" && (
        <>
          <Section title="Details Of Dependents">
            {dependentOptions.length === 0 && <div style={{ color: "#8A93A8" }}>No records found</div>}
            <div className="df-pick-grid">
            {dependentOptions.map((dep) => {
              const active = selectedDependentId === dep.id;
              return (
                <button
                  key={dep.id}
                  type="button"
                  className="w-100 text-start mb-2"
                  onClick={() => setSelectedDependentId(dep.id)}
                  style={{ background: active ? "#fff6f6" : "#fff", border: `1px solid ${active ? BRAND : "#E2E8F0"}`, borderRadius: 8, padding: 12 }}
                >
                  <div className="fw-bold">
                    {dep.firstName} {dep.lastName}
                  </div>
                  <div style={{ color: "#5B6270" }}>
                    {dep.relation} · Date of Birth: {dep.dob}
                  </div>
                  <div style={{ color: "#5B6270" }}>Start Date of dependency: {dep.startDate}</div>
                </button>
              );
            })}
            </div>
          </Section>
          <DocUploader
            required
            title="Upload Required Documents"
            docs={deletionDocuments}
            onAdd={(f) => addDoc(f, true)}
            onRemove={(i) => setDeletionDocuments((p) => p.filter((_, idx) => idx !== i))}
          />
          <Section>
            <Field label="Reason for deletion">
              <TextArea value={deletionReason} placeholder="Enter Reason" onChange={(e) => setDeletionReason(e.target.value)} />
            </Field>
          </Section>
        </>
      )}

      <ActionBar
        left="Cancel"
        right={addFamilyMember === "Deletion" ? "Delete Dependency" : "Submit"}
        busy={saving}
        onLeft={() => navigate(all_routes.idCard.familyMemberInclusion)}
        onRight={handleSave}
      />
    </PageShell>
  );
};

export default FamilyMembersForm;
